/**
 * Month picker options for the Marketing tab, derived from the month string
 * the backend echoes (YYYY-MM, a calendar month in UTC). All of it is string
 * arithmetic on that value, never local-time Date math, so a viewer west of UTC
 * on the 1st cannot see a different month than the backend reports on.
 */
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const parse = (month: string): { y: number; m: number } | null => {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  return m >= 1 && m <= 12 ? { y, m } : null;
};

const format = (y: number, m: number): string => `${y}-${String(m).padStart(2, '0')}`;

/** Shift a YYYY-MM by whole months. */
export const shiftMonth = (month: string, delta: number): string => {
  const p = parse(month);
  if (!p) return month;
  const index = p.y * 12 + (p.m - 1) + delta;
  return format(Math.floor(index / 12), (index % 12) + 1);
};

/** "September 2026" */
export const monthLabel = (month: string): string => {
  const p = parse(month);
  return p ? `${MONTH_NAMES[p.m - 1]} ${p.y}` : month;
};

export interface MonthOption {
  value: string;
  label: string;
}

/**
 * The current month to date ("{Month YYYY} so far"), then the most recent
 * completed month the backend defaulted to, then the 11 before it.
 */
export const buildMonthOptions = (latestCompleted: string, monthsBack = 12): MonthOption[] => {
  if (!parse(latestCompleted)) return [];
  const current = shiftMonth(latestCompleted, 1);
  const options: MonthOption[] = [{ value: current, label: `${monthLabel(current)} so far` }];
  for (let i = 0; i < monthsBack; i += 1) {
    const value = shiftMonth(latestCompleted, -i);
    options.push({ value, label: monthLabel(value) });
  }
  return options;
};

/** Download file name for a section image. */
export const marketingFileName = (key: string, month: string): string => `trademagic-${key}-${month}.png`;
